import { WizTranslationObj, WizTranslationConfigurationObj } from '../obj/translation/wiz-translation.obj';
import { WizLocaleUtils } from '../utils/wiz-locale.utils';
import { WizStringUtils } from '../utils/wiz-string.utils';
import { CORERESOURCES } from '../../assets/core-resources';
import { WizNumberUtils } from '../utils/wiz-number.utils';
import { WizDateUtils } from '../utils/wiz-date.utils';

// gestionnaire de traductions
// singleton
export class WizCoreTranslationManager {

  // les instances de trads
  public static trads: any[] = [CORERESOURCES.en, CORERESOURCES.fr];

  // les compléments de trad issus d'un projet spécifique
  public moreTrads: any[] = null;

  // instance unique
  protected static instance: WizCoreTranslationManager = null;

  /** Separator between key and translation */
  private separator: string;

  /** Comment character to bypass comment lines */
  private commentChar: string;

  /** Default locale, always loaded */
  private defaultLocale: string;

  /** Translation map by translation key */
  protected translationMap: Map<string, WizTranslationObj>;

  /** Character before parameter index inside a translation key */
  private translationVarStart: string;

  /** Character after parameter index inside a translation key */
  private translationVarEnd: string;

  /** Array of current cumulated locales */
  protected _currentLocales: string [];

  /** Array of locales to load */
  protected _loadingLocales: string [];

  /** Locale for date purpose */
  protected _dateLocale: string = 'en';

  public get currentLocales(): string[] {
    const result: string[] = [];
    if (!this._currentLocales) {
      this._currentLocales = [this.defaultLocale];
    }
    for (const locale of this._currentLocales) {
      result.push(locale);
    }
    return result;
  }

  // TODO renommer en currentUsedLocale ...
  public get dateLocale(): string {
    return this._dateLocale;
  }

  // pour appel externe de l'instance
  public static getInstance(): WizCoreTranslationManager {
    if (WizCoreTranslationManager.instance === null) {
      WizCoreTranslationManager.instance = new WizCoreTranslationManager();
      // injection dans WizNumberUtils
      WizNumberUtils.tradInstance = this.instance;
      // injection dans WizDateUtils
      WizDateUtils.tradInstance = this.instance;
    }
    return WizCoreTranslationManager.instance;
  }

  constructor() {
    // uniquement si pas d'instance
    if ( WizCoreTranslationManager.instance ) {
      return;
    }
    // Sets default configuration values
    this.separator = '|';
    this.commentChar = '#';
    this.defaultLocale = 'en';
    this.translationVarStart = '{';
    this.translationVarEnd = '}';
  }

  /**
   * Translation service configuration
   * @param config Configuration object
   */
  configure(config: WizTranslationConfigurationObj): void {
    if (config) {
      if (config.separator) {
        this.separator = config.separator;
      }
      if (config.commentChar !== undefined) {
        this.commentChar = config.commentChar;
      }
      if (config.defaultLocale) {
        this.defaultLocale = config.defaultLocale;
      }
    }
  }

  /* ************************************
        TRANSLATION FILE LOADING
     ************************************ */

  /** Translation loading
   * @param locale locale to be loaded
   */
  async loadTranslations(locale?: string) {
    const arLocales: string[] = [];
    let defaultLocaleSet: boolean;
    if (locale) {
      arLocales.push(locale);
      if (locale === this.defaultLocale) {
        defaultLocaleSet = true;
      }
      const countryCode: string = locale ? locale.substr(0, 2) : null;
      if (countryCode && countryCode !== locale) {
        arLocales.push(countryCode);
        if (countryCode === this.defaultLocale) {
          defaultLocaleSet = true;
        }
      }
    }
    if (!defaultLocaleSet) {
      arLocales.push(this.defaultLocale);
    }
    return this.loadCumulatedTranslations(arLocales);
  }

  /** Cumulated translation loading
   * @param locales array of locales sorted by descendant preference
   */
  async loadCumulatedTranslations(locales: string[]) {
    // let result: Promise<any>;
    if (locales && locales.length > 0) {
      this._loadingLocales = locales;
      this._currentLocales = [...locales]; // array copy
      await this.doLoadTranslations(this._loadingLocales);
    }
    return Promise.resolve();
  }

  /** Loading translations for the last locale in the locale array
   * @param locales array of locales sorted by descendant preference
   */
  async doLoadTranslations(locales: string[]) {
    while (locales.length > 0) {
      let text = null;
      
      const locale: string = locales.pop();
      if(!!locale) {
        // console.log('res', WizTranslationManager.trads, RESOURCES[locale]);
        if(!!CORERESOURCES[locale]) {
          await fetch(CORERESOURCES[locale]).then(async response => {
            text = await response.text();

            if(!!this.moreTrads && this.moreTrads[locale]) { // injection des trads customs sur les projets
              await fetch(this.moreTrads[locale]).then(async projectResponse => {
                let specif = await projectResponse.text();
                // console.log('specif', specif);
                text = [text, specif ].join('');
                return Promise.resolve();
              });
            } else {
              return Promise.resolve();
            }
          });
        }
      }

      await this.translationStringLoaded(text);
    }
    this.refreshTranslations();
    return Promise.resolve();
  }

  /**
   * Refresh of all referenced translatable components
   */
  refreshTranslations(): void {
    this._dateLocale = WizLocaleUtils.getLocale(this._currentLocales);
  }

  /** Translation file loading handler
   * @param cmd File loading command
   */
  translationStringLoaded(trsString?: string) {
    if (trsString) {
      this.populateTranslationMap(trsString.split('\n'));
    }
  }

  /** Populates translation map */
  populateTranslationMap(src: string[]): void {
    if (!this.translationMap) {
      this.translationMap = new Map();
    }
    if (src && src.length > 0) {
      for (const line of src) {
        // if(line.startsWith('$.project.title.$')) {
        //   console.log('line', line);
        // }
        
        // This is not a comment line, put translation into map
        if (!this.commentChar || !line.startsWith(this.commentChar)) {
          const lineAr: string[] = line.toString().split(this.separator);
          if (lineAr.length === 2) {
            if (!this.translationMap.has(lineAr[0])) {
              this.translationMap.set(lineAr[0], new WizTranslationObj(lineAr[0], lineAr[1]));
            } else {
              this.translationMap.get(lineAr[0]).value = lineAr[1];
            }
          }
        }
      }
    }
  }

  /**
   * Returns translated string from a translation key
   * @param pKey Translation key
   * @param pParams Array of values to be included inside the result string.
   * @param orDie valeur par défaut si pas trouvé
   * Values locations are indicated in the translation key by their index between braces ({0], {1}, ...})
   */
  public translateKey(pKey: string, pParams?: string[], orDie?: string): string {
    let result: string = pKey;
    if (this.translationMap.has(pKey)) {
      result = this.translationMap.get(pKey).value;
    }
    if (pParams && pParams.length > 0) {
      const nbParams: number = pParams.length;
      let index: number;
      for (index = 0; index < nbParams; index++) {
        result = result.replace(this.translationVarStart + index + this.translationVarEnd, pParams[index]);
      }
    }

    if((!result || result ===pKey) && orDie) { // gestion de la valeur par défaut
      result = orDie;
    }
    // suppression des \r\n
    if(!!result) {
      result = WizStringUtils.eraseNR(result);
    }
    
    return result;
  }

}
